import React, { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import useOffers from 'api/useOffers';
import useFeatureFlags from 'api/useFeatureFlags';
import usePermissions from 'api/usePermissions';
import { Offer } from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { EFeatureFlag, EPopupSubType, OfferType } from 'constants/enums';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import DialogModal from 'components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from 'components/DialogModalManager/constants/modalConfigs';
import EditOfferDesignModal from 'components/EditOfferDesignModal/EditOfferDesignModal';

import ActionCell from '../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../components/DataTableComponent/DataTable';
import EmptyState from '../../components/EmptyState/EmptyState';
import FilterSearchCard from '../../components/Filter/FilterSearchCard';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import { EToasterStatus, showToast } from '../../utils/showToast';
import { renderActiveStatusChip } from '../../utils/statusChipUtils';

import './style.scss';

const PopupsTable = () => {
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const { canUserEdit } = usePermissions();
  const { isActionEnabled: checkActionEnabled } = useUserPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { page, rows } = usePaginationFromURL('offers');
  const [currentPage, setCurrentPage] = useState(page);
  const { getOffers, deleteOffer, toggleOfferStatus } = useOffers({
    offerType: OfferType.POPUP,
    customPagination: { currentPage, rowsPerPage: rows }
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPopupId, setSelectedPopupId] = useState<string | null>(null);
  const [externalIdFilter, setExternalIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [showLimitedEditModal, setShowLimitedEditModal] = useState(false);

  const totalCount = getOffers.data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / rows);
  const isManageEnabled = useMemo(
    () =>
      checkActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.MANAGE,
        publisherId
      ),
    [publisherId, checkActionEnabled]
  );
  const canUpdateOfferStatus = useMemo(
    () =>
      checkActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.UPDATE_OFFER_STATUS,
        publisherId
      ),
    [publisherId, checkActionEnabled]
  );

  useEffect(() => {
    navigate(`${location.pathname}?page=${currentPage}&rows=${rows}`, {
      replace: true
    });
    getOffers.refetch();
  }, [currentPage, rows]);

  const handleEditOfferUI = (offerId: string): void => {
    navigate(`./form/${offerId}`);
  };

  const handleDuplicateOffer = (offerId: string): void => {
    navigate(`./dup/${offerId}`);
  };

  const handleDeletePopup = (popupId: string) => {
    if (!popupId) return;
    setSelectedPopupId(popupId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedPopupId) return;
    deleteOffer.mutate(selectedPopupId, {
      onSuccess: () => {
        showToast({
          message: 'Popup deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffers.refetch();
        setShowDeleteModal(false);
        setSelectedPopupId(null);
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete popup',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
        setShowDeleteModal(false);
        setSelectedPopupId(null);
      }
    });
  };

  const handleToggleActiveStatus = async (
    offerId: string,
    isActive: boolean
  ) => {
    toggleOfferStatus.mutate(
      { active: isActive, offerId },
      {
        onSuccess: () => {
          showToast({
            message: isActive
              ? 'Popup successfully activated'
              : 'Popup successfully deactivated',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
        },
        onError: (e: any) => {
          showToast({
            message: `Failed to ${isActive ? 'activate' : 'deactivate'} popup`,
            status: EToasterStatus.ERROR,
            description: e.response.data.message ?? ''
          });
        }
      }
    );
  };

  const COLUMNS: any = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'offerId',
      header: 'ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'publisherOfferId',
      header: 'External ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'subType',
      header: 'Type',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'days',
      header: 'Days',
      cell: ({ row }: any) => {
        const filteredProducts = row.original.productsSequence.filter(
          (item: any) => item.products.length > 0
        );
        return filteredProducts?.length > 0 ? filteredProducts?.length : '';
      }
    },
    {
      accessorKey: 'segments',
      header: 'Segments',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'active',
      header: 'Status',
      accessorFn: (row: any) => {
        return row.active ? 'Active' : 'Inactive';
      },
      cell: ({ row }: any) => {
        return renderActiveStatusChip(row.original.active);
      }
    },
    {
      accessorKey: 'actions',
      header: null,
      cell: ({ row }: any) => (
        <ActionCell
          rowData={row.original}
          actions={[
            {
              label: 'Edit',
              handler: () => {
                handleEditOfferUI(row.original.offerId);
              },
              disabled: !isManageEnabled,
              hidden: !checkActionEnabled(
                null,
                EPermissionGroup.OFFERS,
                EPermissionAction.MANAGE,
                publisherId
              )
            },
            {
              label: 'Edit',
              handler: () => {
                setSelectedPopupId(row.original.offerId);
                setShowLimitedEditModal(true);
              },
              disabled:
                !canUserEdit ||
                !checkActionEnabled(
                  null,
                  EPermissionGroup.OFFERS,
                  EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                  publisherId
                ),
              hidden:
                !checkActionEnabled(
                  null,
                  EPermissionGroup.OFFERS,
                  EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                  publisherId
                ) ||
                checkActionEnabled(
                  null,
                  EPermissionGroup.OFFERS,
                  EPermissionAction.MANAGE,
                  publisherId
                )
            },
            {
              label: row.original.active ? 'Deactivate' : 'Activate',
              handler: () =>
                handleToggleActiveStatus(
                  row.original.offerId,
                  !row.original.active
                ),
              disabled: !canUpdateOfferStatus
            },
            {
              label: 'Duplicate',
              handler: () => handleDuplicateOffer(row.original.offerId),
              disabled: !isManageEnabled
            },
            {
              label: 'Delete',
              handler: () => handleDeletePopup(row.original.offerId),
              disabled: !isManageEnabled
            }
          ]}
        />
      )
    }
  ];

  const hasFeatureFlagPostPurchase =
    featureFlags?.[
    EFeatureFlag.DASHBOARD_POST_PURCHASE
    ];

  const offers: Offer[] = useMemo(() => {
    if (getOffers.isLoading || !getOffers.data) {
      return [];
    }

    if (getOffers.data.offers) {
      return getOffers.data.offers
        .map((offer: Offer) => ({
          _id: offer.publisherOfferId,
          ...offer
        }))
        .filter((offer: Offer) =>
          hasFeatureFlagPostPurchase
            ? true
            : offer.subType !== EPopupSubType.POST_PURCHASE
        );
    }

    if (getOffers.data.result) {
      return getOffers.data.result
        .filter((offer: Offer) => offer.type === 'SpecialOffer')
        .map((offer: Offer) => ({
          _id: offer.publisherOfferId,
          ...offer
        }));
    }
    return [];
  }, [getOffers.isLoading, getOffers.data, hasFeatureFlagPostPurchase]);

  const filteredOffers = useMemo(() => {
    return offers.filter((offer) => {
      const lowerExternalId = offer.publisherOfferId.toLowerCase();
      const lowerName = offer.name.toLowerCase();
      return (
        lowerExternalId.includes(externalIdFilter.toLowerCase()) &&
        lowerName.includes(nameFilter.toLowerCase())
      );
    });
  }, [offers, externalIdFilter, nameFilter]);

  const BUTTON_TEXT = `${offers.length > 0 ? 'Add' : 'Create'} New Popup`;

  const renderContent = () => {
    if (getOffers.isLoading || getOffers.isFetching) {
      return (
        <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />
      );
    }

    if (offers.length === 0) {
      return (
        <EmptyState
          title="Add your first Popup"
          description="Rewards that appear as popups when players open the webstore, encouraging them to return regularly"
          buttonText={BUTTON_TEXT}
          disableButton={!checkActionEnabled(
            null,
            EPermissionGroup.OFFERS,
            EPermissionAction.MANAGE,
            publisherId
          )
          }
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
        />
      );
    }

    return (
      <div className="border border-base-sidebarBorder rounded-lg mx-7 mb-9">
        <DataTable
          data={filteredOffers}
          columns={COLUMNS}
          rowIdIdentifier="offerId"
          bodyDataTestId="dashPopupsList"
          rowCount={totalCount}
          rowsPerPage={rows}
          showPagination={totalPages > 1}
          manualPagination
          pageIndex={currentPage}
          pageCount={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    );
  };

  return (
    <div className="overflow-y-scroll">
      <PortalHeader
        title="Popups"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={() => navigate('./form')}
        primaryButtonState={{
          disabled: !canUserEdit,
          hidden: !checkActionEnabled(
            null,
            EPermissionGroup.OFFERS,
            EPermissionAction.MANAGE,
            publisherId
          )
        }}
        isCreateButton
      />
      {offers.length > 0 && (
        <div className="flex px-7 gap-3 mb-5">
          <FilterSearchCard
            header="Popups External ID"
            filterValue={externalIdFilter}
            setFilterValue={setExternalIdFilter}
            isLabelSearchBy
          />
          <FilterSearchCard
            header="Popups Name"
            filterValue={nameFilter}
            setFilterValue={setNameFilter}
          />
        </div>
      )}
      {renderContent()}
      {showLimitedEditModal && (
        <EditOfferDesignModal
          offerId={selectedPopupId || ''}
          offerDesignType={OfferDesignTypes.PopUp}
          onClose={() => setShowLimitedEditModal(false)}
          isOpen={showLimitedEditModal}
          offerType={OfferType.POPUP}
        />
      )}
      {showDeleteModal && (
        <DialogModal
          isOpen={showDeleteModal}
          onOpenChange={(open) => {
            if (!open) setShowDeleteModal(false);
          }}
          title={modalConfigs[PORTAL_MODAL_NAMES.DELETE_TABLE_POPUP].title}
          description={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_TABLE_POPUP].description
          }
          modalActions={{
            onConfirm: { label: 'Delete', action: handleConfirmDelete },
            onClose: {
              label: 'Cancel',
              action: () => setShowDeleteModal(false),
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default PopupsTable;
